/* Reservation reminders, shared by the three organizer verticals (Beauty, courts, tables).
 *
 * Settings: Beauty, Club and Bar store `reminders: { enabled, hoursBefore }` with the same shape. `enabled`
 * switches every channel at once (today only email). Delivery: every reservation payload carries
 * `reminder: { channels: { <name>: { state, at, reason } } } | null`. */

import { store } from "../../redux/store/store";

export const HORAS_ANTES_RECORDATORIO = [2, 12, 24, 48];
export const HORAS_ANTES_POR_DEFECTO = 24;

export const IDIOMA_WHATSAPP = "es_AR";

/** Whether a session may create WhatsApp templates: the owner (any session that is not an employee) or a Chat admin. */
export const puedeCrearPlantillas = (auth) => auth?.user?.tipo !== "empleado" || auth?.user?.perms?.chats?.rol === "administrador";

/** Same rule for the current session. Read from the store directly, like the API helpers do. */
export const usePuedeCrearPlantillas = () => puedeCrearPlantillas(store.getState().auth);

/** Data a WhatsApp template variable can carry. */
export const CAMPOS_WHATSAPP = [
  { value: "clientName", label: "Nombre del cliente" },
  { value: "venueName", label: "Local" },
  { value: "dateLabel", label: "Fecha" },
  { value: "timeLabel", label: "Hora" },
  { value: "details", label: "Servicio o detalle" },
  { value: "address", label: "Dirección" },
  { value: "manageUrl", label: "Link para reagendar" },
];
/** Fields of the review request template: the reminder ones without the reschedule link, plus the review link. */
export const CAMPOS_WHATSAPP_RESENA = [
  ...CAMPOS_WHATSAPP.filter((c) => c.value !== "manageUrl"),
  { value: "reviewUrl", label: "Link para opinar" },
];
/** Fields of the waitlist notice template: the venue and slot data, plus the link to claim the slot. */
export const CAMPOS_WHATSAPP_LISTA_ESPERA = [
  ...CAMPOS_WHATSAPP.filter((c) => c.value !== "manageUrl"),
  { value: "claimUrl", label: "Link para reservar" },
];
/** Fields of the client campaign templates (birthday, win-back). */
export const CAMPOS_WHATSAPP_CAMPANA = [
  { value: "clientName", label: "Nombre del cliente" },
  { value: "businessName", label: "Nombre del negocio" },
  { value: "venueName", label: "Último local" },
  { value: "bookingUrl", label: "Link para reservar" },
  { value: "unsubscribeUrl", label: "Link para darse de baja" },
];
// Default mapping when a template is picked: the fields it usually needs, then the rest (only those the list offers).
const ORDEN_POR_DEFECTO = ["clientName", "businessName", "venueName", "dateLabel", "timeLabel", "manageUrl", "reviewUrl", "claimUrl", "bookingUrl", "unsubscribeUrl", "details", "address"];
const valoresDe = (campos) => campos.map((c) => c.value);
const CAMPOS_VALIDOS = valoresDe(CAMPOS_WHATSAPP);

export const PUSH_TITULO_MAX = 65;
export const PUSH_TEXTO_MAX = 180;
export const PUSH_VARIABLES = ["{cliente}", "{local}", "{fecha}", "{hora}"];
/** Default in-app texts, kept in one place so they stay aligned with the back's. Keys: reminder, review, waitlist. */
export const PUSH_TEXTOS_POR_DEFECTO = {
  review: { title: "¿Cómo te fue en {local}?", body: "Contanos tu experiencia, nos ayuda mucho." },
  waitlist: { title: "Se liberó un lugar en {local}", body: "{fecha}, {hora}. Reservalo antes de que se ocupe." },
};
/** Default texts for a block; the reminder's title uses the vertical's noun ("turno" in Salón, "reserva" in Club/Bar). */
export const pushTextosPorDefecto = (tipo, sustantivo = "reserva") =>
  tipo === "reminder"
    ? { title: `Recordatorio: tu ${sustantivo} en {local}`, body: "{fecha} {hora}" }
    : PUSH_TEXTOS_POR_DEFECTO[tipo] || { title: "", body: "" };

/**
 * Form value from a `push` block. The in-app notification is off unless the venue turned it on; a missing key is off.
 * A title/body the back didn't send stays out, so the form can prefill the default texts.
 */
export const pushDeVenue = (push) => {
  const valor = { enabled: push?.enabled === true };
  if (typeof push?.title === "string") valor.title = push.title;
  if (typeof push?.body === "string") valor.body = push.body;
  return valor;
};

/** The `push` value to save: texts travel as written, `""` when the venue never had any (the back then uses its default). */
const pushJson = (push) => {
  const { enabled, title, body } = pushDeVenue(push);
  return { enabled, title: title ?? "", body: body ?? "" };
};

/** Form value from `reminders.whatsapp`. A missing key (the venue never saved it) is the disabled default. */
export const whatsappDeVenue = (whatsapp, campos = CAMPOS_WHATSAPP) => ({
  enabled: whatsapp?.enabled === true,
  workspaceId: whatsapp?.workspaceId || "",
  templateName: whatsapp?.templateName || "",
  language: whatsapp?.language || IDIOMA_WHATSAPP,
  parameters: Array.isArray(whatsapp?.parameters) ? whatsapp.parameters.filter((p) => valoresDe(campos).includes(p)) : [],
});

/** Mapping for a template with `count` variables: the fields in their usual order. */
export const parametrosPorDefecto = (count, campos = CAMPOS_WHATSAPP) => {
  const orden = ORDEN_POR_DEFECTO.filter((c) => valoresDe(campos).includes(c));
  return orden.slice(0, Math.min(Math.max(count, 0), orden.length));
};

/** Why a Zoco Chat workspace can't send templates, or "" when it can (or Meta could not be asked). */
export const bloqueoDeWorkspace = (workspace) => {
  if (workspace?.metodoDePago === "falta") return "Esta cuenta de WhatsApp no tiene un método de pago: cargalo en el Business Manager de Meta para activar los recordatorios por WhatsApp";
  if (workspace?.puedeEnviar === "BLOCKED") return "Meta bloqueó los envíos de esta cuenta de WhatsApp: no se pueden activar los recordatorios por WhatsApp";
  return "";
};

/** Why the WhatsApp settings can't be saved, or "" when they can. Only an enabled channel is validated. */
export const faltaWhatsapp = (whatsapp) => {
  if (!whatsapp?.enabled) return "";
  if (whatsapp.bloqueo) return whatsapp.bloqueo;
  if (!whatsapp.workspaceId) return "Elegí el workspace de Zoco Chat para los recordatorios por WhatsApp";
  if (!whatsapp.templateName) return "Elegí la plantilla de WhatsApp para los recordatorios";
  if ((whatsapp.parameters || []).some((p) => !CAMPOS_VALIDOS.includes(p))) {
    return "Elegí qué dato va en cada variable de la plantilla de WhatsApp";
  }
  return "";
};

export const HORAS_DESPUES_RESENA = [2, 24, 48];
export const HORAS_DESPUES_POR_DEFECTO = 2;

/** Form value from `reminders.review`. A venue that never saved it has the request off, 2 hours after. */
export const resenaDeVenue = (review) => {
  const horas = Number(review?.hoursAfter);
  return {
    enabled: review?.enabled === true,
    hoursAfter: HORAS_DESPUES_RESENA.includes(horas) ? horas : HORAS_DESPUES_POR_DEFECTO,
    whatsapp: whatsappDeVenue(review?.whatsapp, CAMPOS_WHATSAPP_RESENA),
    push: pushDeVenue(review?.push),
  };
};

/** Why the review request can't be saved, or "" when it can. Only an enabled request with WhatsApp on is validated. */
export const faltaResena = (resena) => {
  const wa = resena?.whatsapp;
  if (!resena?.enabled || !wa?.enabled) return "";
  if (wa.bloqueo) return wa.bloqueo;
  if (!wa.workspaceId) return "Elegí el workspace de Zoco Chat para el pedido de reseña por WhatsApp";
  if (!wa.templateName) return "Elegí la plantilla de WhatsApp para el pedido de reseña";
  const validos = valoresDe(CAMPOS_WHATSAPP_RESENA);
  if ((wa.parameters || []).some((p) => !validos.includes(p))) {
    return "Elegí qué dato va en cada variable de la plantilla de WhatsApp";
  }
  return "";
};

/** Form value from `reminders.waitlist`. A venue that never saved it has the waitlist off. */
export const listaEsperaDeVenue = (waitlist) => ({
  enabled: waitlist?.enabled === true,
  whatsapp: whatsappDeVenue(waitlist?.whatsapp, CAMPOS_WHATSAPP_LISTA_ESPERA),
  push: pushDeVenue(waitlist?.push),
});

/** Why the waitlist notice can't be saved, or "" when it can. Only an enabled waitlist with WhatsApp on is validated. */
export const faltaListaEspera = (lista) => {
  const wa = lista?.whatsapp;
  if (!lista?.enabled || !wa?.enabled) return "";
  if (wa.bloqueo) return wa.bloqueo;
  if (!wa.workspaceId) return "Elegí el workspace de Zoco Chat para el aviso de la lista de espera por WhatsApp";
  if (!wa.templateName) return "Elegí la plantilla de WhatsApp para el aviso de la lista de espera";
  const validos = valoresDe(CAMPOS_WHATSAPP_LISTA_ESPERA);
  if ((wa.parameters || []).some((p) => !validos.includes(p))) {
    return "Elegí qué dato va en cada variable de la plantilla de WhatsApp";
  }
  return "";
};

/** Form values from a venue's `reminders`. A venue without the setting (legacy) has them on, 24 hours before. */
export const recordatoriosDeVenue = (reminders) => {
  const horas = Number(reminders?.hoursBefore);
  return {
    recordatoriosActivos: reminders?.enabled !== false,
    horasAntesRecordatorio: HORAS_ANTES_RECORDATORIO.includes(horas) ? horas : HORAS_ANTES_POR_DEFECTO,
    whatsapp: whatsappDeVenue(reminders?.whatsapp),
    push: pushDeVenue(reminders?.push),
    resena: resenaDeVenue(reminders?.review),
    listaEspera: listaEsperaDeVenue(reminders?.waitlist),
  };
};

const whatsappJson = (wa) => ({
  enabled: Boolean(wa.enabled),
  workspaceId: wa.workspaceId || "",
  templateName: wa.templateName || "",
  language: wa.language || IDIOMA_WHATSAPP,
  parameters: wa.parameters || [],
});

/** The `reminders` value for a form: one JSON string, like `rescheduling`, so multipart carries it as a unit. */
export const recordatoriosJson = (form = {}) => {
  const { recordatoriosActivos, horasAntesRecordatorio } = recordatoriosDeVenue({
    enabled: form.recordatoriosActivos,
    hoursBefore: form.horasAntesRecordatorio,
  });
  // `push` always travels, like the review and waitlist ones, so turning it off is saved too.
  const body = { enabled: recordatoriosActivos, hoursBefore: horasAntesRecordatorio, push: pushJson(form.push) };
  // Only a venue that configured WhatsApp sends it; the back merges partial updates, so the rest keeps what it had.
  const wa = form.whatsapp;
  if (wa && (wa.enabled || wa.workspaceId || wa.templateName)) {
    body.whatsapp = whatsappJson(wa);
  }
  // The review request travels in the same JSON; a form without it (never loaded) leaves the venue's untouched.
  if (form.resena) {
    const { enabled, hoursAfter } = resenaDeVenue(form.resena);
    const review = { enabled, hoursAfter, push: pushJson(form.resena.push) };
    const rwa = form.resena.whatsapp;
    if (rwa && (rwa.enabled || rwa.workspaceId || rwa.templateName)) review.whatsapp = whatsappJson(rwa);
    body.review = review;
  }
  // Same for the waitlist: WhatsApp only travels when configured.
  if (form.listaEspera) {
    const waitlist = { enabled: listaEsperaDeVenue(form.listaEspera).enabled, push: pushJson(form.listaEspera.push) };
    const lwa = form.listaEspera.whatsapp;
    if (lwa && (lwa.enabled || lwa.workspaceId || lwa.templateName)) waitlist.whatsapp = whatsappJson(lwa);
    body.waitlist = waitlist;
  }
  return JSON.stringify(body);
};

/**
 * Adds `reminders` to a whole-form multipart request (club and bar send every setting each time). A form that
 * never had the setting (a guided first run built from scratch) sends nothing, so the back default applies.
 */
export const appendRecordatorios = (formData, form = {}) => {
  if (form.recordatoriosActivos === undefined && form.horasAntesRecordatorio === undefined && form.whatsapp === undefined && form.push === undefined && form.resena === undefined && form.listaEspera === undefined) return;
  formData.append("reminders", recordatoriosJson(form));
};

// Channel names as the back reports them (reminder.channels.<name>). An unknown name is shown as-is, so a new
// channel (a future one) appears in every detail without touching this file.
const CANAL = { email: "por email", whatsapp: "por WhatsApp" };

// `reason` codes from the back. An unknown one is left out of the line on purpose: it could carry provider text.
const MOTIVO = {
  booked_inside_window: "se reservó dentro de la ventana del recordatorio",
  email_missing: "el cliente no tiene email",
  missing_email: "el cliente no tiene email",
  no_email: "el cliente no tiene email",
  reminders_disabled: "los recordatorios están desactivados",
  appointment_started: "la reserva ya había comenzado",
  checked_in: "el cliente ya hizo check-in",
  already_checked_in: "el cliente ya hizo check-in",
  delivery_failed: "falló el envío",
  send_failed: "falló el envío",
  stale_sending: "el envío no se completó",
  max_attempts: "se alcanzó el límite de reintentos",
  chat_not_configured: "Zoco Chat no está configurado",
  whatsapp_disabled: "el recordatorio por WhatsApp está desactivado",
  whatsapp_not_configured: "falta elegir el workspace o la plantilla de WhatsApp",
  phone_missing: "el cliente no tiene celular",
  phone_invalid: "el celular del cliente no es válido",
  whatsapp_opted_out: "el cliente no quiere recibir mensajes por WhatsApp",
  whatsapp_invalid_template: "la plantilla de WhatsApp no es válida",
  whatsapp_invalid_recipient: "el celular del cliente no tiene WhatsApp",
  whatsapp_no_connection: "el workspace no tiene WhatsApp conectado",
  whatsapp_workspace_not_found: "no se encontró el workspace de Zoco Chat",
  whatsapp_rejected: "WhatsApp rechazó el mensaje",
  whatsapp_no_negocio: "el local no está vinculado a un negocio de Zoco",
};

const fechaHora = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "short", timeStyle: "short" }).format(parsed);
};

const lineaDeCanal = (name, channel) => {
  const label = `Recordatorio ${CANAL[name] || name}`;
  const state = channel.state || null;
  if (state === "sent") {
    const at = fechaHora(channel.at);
    return at ? `${label} enviado ${at}` : `${label} enviado`;
  }
  if (state === null || state === "sending") return `${label} pendiente`;
  if (state === "skipped" || state === "failed") {
    const motivo = MOTIVO[channel.reason];
    return motivo ? `${label} no enviado: ${motivo}` : `${label} no enviado`;
  }
  return "";
};

/** One line per channel of `reservation.reminder`. `null`, an empty object or an unknown state gives no line. */
export const lineasDeRecordatorio = (reminder) => {
  const channels = reminder?.channels;
  if (!channels || typeof channels !== "object") return [];
  return Object.entries(channels)
    .filter(([name, channel]) => name && channel && typeof channel === "object")
    .map(([name, channel]) => ({ name, text: lineaDeCanal(name, channel) }))
    .filter((line) => line.text);
};

/** Why the WhatsApp channel of a client campaign can't be saved, or "" when it can. Only an enabled channel is validated. */
export const faltaWhatsappCampana = (wa) => {
  if (!wa?.enabled) return "";
  if (wa.bloqueo) return wa.bloqueo;
  if (!wa.workspaceId) return "Elegí el workspace de Zoco Chat para la campaña por WhatsApp.";
  if (!wa.templateName) return "Elegí la plantilla de WhatsApp para la campaña.";
  const validos = valoresDe(CAMPOS_WHATSAPP_CAMPANA);
  if ((wa.parameters || []).some((p) => !validos.includes(p))) {
    return "Elegí qué dato va en cada variable de la plantilla de WhatsApp.";
  }
  return "";
};
